import { getAddress, isAddress, type Address } from "viem";
import { baseClient, ethereumClient } from "./chain";
import {
  BURN_TOKEN,
  HARDWARE_COLLECTION,
  HARDWARE_METADATA_BASE_URI,
  LEGACY_MINTER,
  burnerAbi,
  hardwareCollectionAbi,
  receiverAbi,
} from "./contracts";

export type DeploymentConfig = {
  burnerAddress?: Address;
  burnerDeploymentBlock?: string;
  receiverAddress?: Address;
  receiverDeploymentBlock?: string;
};

const STORAGE_KEY = "hardware:deployment";
const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";

export function loadDeploymentConfig(): DeploymentConfig {
  let stored: Partial<DeploymentConfig> = {};
  try {
    stored = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "{}");
  } catch {
    // Ignore malformed local configuration and fall back to the published build values.
  }

  const envBurner = import.meta.env.VITE_BASE_BURNER_ADDRESS;
  const envReceiver = import.meta.env.VITE_ETH_RECEIVER_ADDRESS;
  return {
    burnerAddress: validAddress(envBurner) ?? validAddress(stored.burnerAddress),
    burnerDeploymentBlock: import.meta.env.VITE_BASE_DEPLOYMENT_BLOCK ?? stored.burnerDeploymentBlock,
    receiverAddress: validAddress(envReceiver) ?? validAddress(stored.receiverAddress),
    receiverDeploymentBlock: import.meta.env.VITE_ETH_RECEIVER_DEPLOYMENT_BLOCK ?? stored.receiverDeploymentBlock,
  };
}

export function saveDeploymentConfig(config: DeploymentConfig) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
}

export function isDeploymentConfigured(config: DeploymentConfig) {
  return Boolean(config.burnerAddress && config.receiverAddress);
}

export async function discoverDeploymentConfig(
  options: { requireActivation?: boolean } = {},
): Promise<DeploymentConfig> {
  const current = loadDeploymentConfig();
  const [minters, collectionOwner, baseUri] = await Promise.all([
    ethereumClient.readContract({ address: HARDWARE_COLLECTION, abi: hardwareCollectionAbi, functionName: "minters" }),
    ethereumClient.readContract({ address: HARDWARE_COLLECTION, abi: hardwareCollectionAbi, functionName: "owner" }),
    ethereumClient.readContract({ address: HARDWARE_COLLECTION, abi: hardwareCollectionAbi, functionName: "baseURI" }),
  ]);
  const inactive = { ...current, burnerAddress: undefined, receiverAddress: undefined };
  if (options.requireActivation) {
    const legacyStillActive = minters.some((minter) => minter.toLowerCase() === LEGACY_MINTER.toLowerCase());
    const metadataStillExternal = baseUri.replace(/\/$/, "") !== HARDWARE_METADATA_BASE_URI;
    if (legacyStillActive || metadataStillExternal || minters.length !== 1) return inactive;
  }

  const candidates = await Promise.allSettled(minters.map(async (receiverAddress) => {
    const [receiverOwner, hardware, sourceChainId, sourceContract, recovered230, recovered231, receiverMaximum] = await Promise.all([
      ethereumClient.readContract({ address: receiverAddress, abi: receiverAbi, functionName: "owner" }),
      ethereumClient.readContract({ address: receiverAddress, abi: receiverAbi, functionName: "hardware" }),
      ethereumClient.readContract({ address: receiverAddress, abi: receiverAbi, functionName: "sourceChainId" }),
      ethereumClient.readContract({ address: receiverAddress, abi: receiverAbi, functionName: "sourceContract" }),
      ethereumClient.readContract({ address: receiverAddress, abi: receiverAbi, functionName: "legacyRecovered", args: [230n] }),
      ethereumClient.readContract({ address: receiverAddress, abi: receiverAbi, functionName: "legacyRecovered", args: [231n] }),
      ethereumClient.readContract({ address: receiverAddress, abi: receiverAbi, functionName: "MAX_REDEMPTION_NUMBER" }),
    ]);
    if (
      receiverOwner.toLowerCase() !== collectionOwner.toLowerCase()
      || hardware.toLowerCase() !== HARDWARE_COLLECTION.toLowerCase()
      || sourceChainId !== 8453n
      || receiverMaximum !== 544n
    ) {
      throw new Error("Not the Hardware recovery receiver.");
    }
    if (options.requireActivation && (!recovered230 || !recovered231)) {
      throw new Error("Historical recovery is not complete.");
    }

    const [burnerOwner, burnToken, burnTokenId, redemptionCount, deploymentBlock, burnerMaximum] = await Promise.all([
      baseClient.readContract({ address: sourceContract, abi: burnerAbi, functionName: "owner" }),
      baseClient.readContract({ address: sourceContract, abi: burnerAbi, functionName: "burnToken" }),
      baseClient.readContract({ address: sourceContract, abi: burnerAbi, functionName: "burnTokenId" }),
      baseClient.readContract({ address: sourceContract, abi: burnerAbi, functionName: "redemptionCount" }),
      baseClient.readContract({ address: sourceContract, abi: burnerAbi, functionName: "deploymentBlock" }),
      baseClient.readContract({ address: sourceContract, abi: burnerAbi, functionName: "MAX_REDEMPTION_NUMBER" }),
    ]);
    if (
      burnerOwner.toLowerCase() !== collectionOwner.toLowerCase()
      || burnToken.toLowerCase() !== BURN_TOKEN.toLowerCase()
      || burnTokenId !== 1n
      || redemptionCount < 231n
      || burnerMaximum !== 544n
    ) {
      throw new Error("Receiver points to an unexpected Base burner.");
    }
    return { receiverAddress, burnerAddress: sourceContract, burnerDeploymentBlock: deploymentBlock.toString() };
  }));

  const discoveredCandidates = candidates
    .filter((candidate): candidate is PromiseFulfilledResult<{ receiverAddress: Address; burnerAddress: Address; burnerDeploymentBlock: string }> => candidate.status === "fulfilled")
    .map((candidate) => candidate.value);
  if (options.requireActivation && discoveredCandidates.length !== 1) return inactive;
  const discovered = discoveredCandidates.at(-1);
  if (!discovered) return options.requireActivation ? inactive : current;

  const next = { ...current, ...discovered };
  saveDeploymentConfig(next);
  return next;
}

export function validAddress(value: unknown): Address | undefined {
  return typeof value === "string" && isAddress(value) && value.toLowerCase() !== ZERO_ADDRESS
    ? getAddress(value)
    : undefined;
}
